import React, { memo, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import styledContainerQuery from 'styled-container-query';
import { useObserver } from 'mobx-react-lite';
import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import type { DutchiePosLoyaltyConfig } from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useModals, ModalNames } from 'src/components/modals';
import { usePersonalizationEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import BlueSparkle from 'src/assets/blue-sparkle-icon';
import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import { DposLoyaltyDefault as DposLoyaltyIcon } from 'src/assets/dpos-loyalty-default';
import { AppleLogin, GoogleLogin } from 'src/components/social-login';
import { tracker } from 'src/utils/analytics';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';

type TrackEventProps = {
  signIn: boolean;
  type: 'email' | 'sso';
};

export type IsDposLoyaltyCtaEnabledInput = {
  isIntegrationEnabled: boolean | undefined;
  showLoyaltyRewards: boolean | undefined;
  isShowNewLoyaltyConfigOptionsFlagEnabled: boolean;
  loyaltyConfig: DutchiePosLoyaltyConfig | null | undefined;
};

/** Pure visibility rule for the loyalty variant of the login CTA, gated on merchant display settings. */
export function getIsDposLoyaltyCtaEnabled({
  isIntegrationEnabled,
  showLoyaltyRewards,
  isShowNewLoyaltyConfigOptionsFlagEnabled,
  loyaltyConfig,
}: IsDposLoyaltyCtaEnabledInput): boolean {
  return !!(
    isIntegrationEnabled &&
    showLoyaltyRewards &&
    (isShowNewLoyaltyConfigOptionsFlagEnabled ? loyaltyConfig?.loyaltyWidgetEnabledOnWeb ?? true : true)
  );
}

type GetCtaDetailsReturn = {
  heading: string;
  body?: string;
  icon?: ReactNode;
  testId: string;
};

const getCtaDetails = (
  isDposLoyaltyEnabled: boolean,
  isPersonalizationEnabled: boolean,
  rewardsProgramName: string | null | undefined
): GetCtaDetailsReturn => {
  if (isPersonalizationEnabled && isDposLoyaltyEnabled) {
    return {
      heading: 'Rewards and personalization in one seamless experience.',
      body: 'Enjoy personalized recommendations, faster checkout, and earn points with every purchase.',
      icon: <BlackSparkleIcon height={28} width={28} fill='#fff' />,
      testId: 'login-cta-personalization-loyalty',
    };
  }

  if (isPersonalizationEnabled && !isDposLoyaltyEnabled) {
    return {
      heading: 'Log in for the best experience',
      body: 'Enjoy personalized recommendations, faster checkout, and quick reordering of your favorites.',
      icon: <BlueSparkle />,
      testId: 'login-cta-personalization-no-loyalty',
    };
  }

  if (!isPersonalizationEnabled && isDposLoyaltyEnabled) {
    return {
      heading: rewardsProgramName ?? 'Rewards',
      body: 'Earn points on every purchase and unlock exclusive rewards. Sign up today and start earning points!',
      icon: (
        <DposIconWrapper>
          <DposLoyaltyIcon />
        </DposIconWrapper>
      ),
      testId: 'login-cta-no-personalization-loyalty',
    };
  }

  return {
    heading: 'Login for easy checkout and re-ordering',
    testId: 'login-cta-no-personalization-no-loyalty',
  };
};

type LoginV2Props = {
  hideIfNeitherEnabled?: boolean;
  margin?: string;
};

const LoginV2Component = ({ hideIfNeitherEnabled = false, margin }: LoginV2Props): JSX.Element | null => {
  const { openModal } = useModals();
  // Broad, non-Pay login CTA: stamp an existing session token but never mint one just to emit this event.
  const { trackLoginEntryPointClicked } = useDutchiePayAnalytics({ mintSessionToken: false });
  const User = useUser();
  const { isKiosk } = useUI();

  // window is unavailable during SSR, so seed empty and resolve the redirect target after mount.
  // This keeps the server render and first client render identical (clean hydration); the real
  // href is populated well before the user can trigger a social login.
  const [redirectUrl, setRedirectUrl] = useState('');
  useEffect(() => {
    setRedirectUrl(window.location.href);
  }, []);
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isUserLoading = useObserver(() => User.loading);
  const isUserLoadedAndLoggedIn = !isUserLoading && isLoggedIn;

  const isPersonalizationEnabled = usePersonalizationEnabled();
  const { dispensary } = useDispensary();
  const isIntegrationEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;
  const showLoyaltyRewards = dispensary?.storeSettings?.showLoyaltyRewards;

  const flags = useFlags();
  const isShowNewLoyaltyConfigOptionsFlagEnabled =
    flags['pos.backoffice.show-new-loyalty-config-options.rollout'] ?? false;

  const skipLoyaltyConfigQuery =
    !dispensary?.id || isKiosk || isUserLoadedAndLoggedIn || !isIntegrationEnabled || !showLoyaltyRewards;

  const { data, loading: isLoyaltyConfigLoading } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip: skipLoyaltyConfigQuery,
  });

  const isDposLoyaltyEnabled = getIsDposLoyaltyCtaEnabled({
    isIntegrationEnabled,
    showLoyaltyRewards,
    isShowNewLoyaltyConfigOptionsFlagEnabled,
    loyaltyConfig: data?.getDutchiePosLoyaltyConfig,
  });

  const shouldHideCta =
    isKiosk || isUserLoadedAndLoggedIn || (hideIfNeitherEnabled && !isPersonalizationEnabled && !isDposLoyaltyEnabled);

  if (shouldHideCta) {
    return null;
  }

  if (isUserLoading || (!skipLoyaltyConfigQuery && isLoyaltyConfigLoading)) {
    return null;
  }

  const { heading, body, icon, testId } = getCtaDetails(
    isDposLoyaltyEnabled,
    isPersonalizationEnabled,
    data?.getDutchiePosLoyaltyConfig?.onlineName
  );

  const handleLogin = (): void => {
    void openModal(ModalNames.login);
    trackEvent({ signIn: true, type: 'email' });
    trackLoginEntryPointClicked({ source: 'menu_login_cta' });
  };

  const trackEvent = ({ signIn, type }: TrackEventProps): void => {
    tracker.gaGTMClickEvent({
      eventCategory: 'account',
      eventAction: signIn ? 'signed in' : 'created account',
      eventLabel: `${type}: {pageType}`,
    });
  };

  return (
    <Wrapper $isDposLoyaltyEnabled={isDposLoyaltyEnabled} $margin={margin} data-testid={testId}>
      <Content $shouldReverseIcon={icon && !(isDposLoyaltyEnabled && !isPersonalizationEnabled)}>
        {icon && <Icon>{icon}</Icon>}

        <div>
          <Heading>{heading}</Heading>

          {body && <Body>{body}</Body>}
        </div>
      </Content>

      <Actions>
        <Buttons>
          <ButtonWrapper>
            <GoogleLogin redirectUrl={redirectUrl} onSubmit={() => trackEvent({ signIn: true, type: 'sso' })} />
          </ButtonWrapper>

          <ButtonWrapper>
            <AppleLogin redirectUrl={redirectUrl} onSubmit={() => trackEvent({ signIn: true, type: 'sso' })} />
          </ButtonWrapper>
        </Buttons>

        <InlineLink onClick={handleLogin}>Log in or sign up with email</InlineLink>
      </Actions>
    </Wrapper>
  );
};

export const LoginV2 = memo(LoginV2Component);
export default LoginV2;

const Content = styled.div<{ $shouldReverseIcon?: boolean }>`
  display: flex;
  flex: 1 1;
  flex-direction: ${({ $shouldReverseIcon }) => ($shouldReverseIcon ? 'row-reverse' : 'row')};
  gap: 16px;
`;

const Icon = styled.div`
  flex: 0 0;
`;

const DposIconWrapper = styled.div`
  border-radius: 50%;
  color: var(--dcm-color-primary);
  overflow: hidden;

  svg {
    display: block;
  }
`;

const Heading = styled.h3`
  color: var(--heading-color);
  font-size: 20px;
  font-weight: 600;
  line-height: ${24 / 16};
`;

const Body = styled.p`
  font-size: 16px;
  line-height: ${24 / 16};
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonWrapper = styled.div`
  flex: 1;
`;

const InlineLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  color: var(--link-color);
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: ${24 / 14};
  padding: 0;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const Wrapper = styledContainerQuery.div<{ $isDposLoyaltyEnabled: boolean; $margin?: string }>`
  --body-color: ${({ $isDposLoyaltyEnabled }) =>
    $isDposLoyaltyEnabled ? 'var(--dcm-color-text-inverted)' : 'var(--dcm-logged-out-cta-subtext-color)'};
  --heading-color: ${({ $isDposLoyaltyEnabled }) =>
    $isDposLoyaltyEnabled ? 'var(--dcm-color-text-inverted)' : 'var(--dcm-logged-out-cta-text-color)'};
  --link-color: ${({ $isDposLoyaltyEnabled }) =>
    $isDposLoyaltyEnabled ? 'var(--dcm-color-text-inverted)' : 'var(--dcm-logged-out-cta-link-color)'};

  background: ${({ $isDposLoyaltyEnabled }) =>
    $isDposLoyaltyEnabled ? 'var(--dcm-color-primary)' : 'var(--dcm-logged-out-cta-bg)'};
  border: 1px solid var(--dcm-logged-out-cta-border-color);
  border-radius: var(--dcm-logged-out-cta-border-radius);
  color: var(--body-color);
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: ${({ $margin }) => $margin ?? '0 auto'};
  max-width: 1200px;
  overflow: hidden;
  padding: 24px;
  position: relative;
  z-index: 1;

  &::before {
    background: #ffffff1a;
    border-radius: 50%;
    content: '';
    height: 400px;
    left: 52px;
    pointer-events: none;
    position: absolute;
    top: 52px;
    transform: translate(-50%, -50%);
    width: 400px;
    z-index: -1;
  }

  &:container(min-width: 750px) {
    align-items: center;
    flex-direction: row;
    gap: 64px;

    ${Content} {
      flex-direction: row;
      max-width: 536px;
    }

    ${InlineLink} {
      display: flex;
    }
  }

  &:container(min-width: 1010px) {
    ${Actions} {
      flex: 1 0;
      gap: 8px;
    }

    ${Buttons} {
      align-items: center;
      flex-direction: row;
      gap: 12px;
    }
  }
`;
